import { useMutation } from '@apollo/client';
import { faArrowUpFromBracket, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Avatar, { initialsOf } from '../../core/components/Avatar.tsx';
import Button from '../../core/components/Button.tsx';
import { Tag } from '../../core/components/Fields/TagsField.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import Popover from '../../core/components/Popover.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';
import { graphqlErrorMessage } from '../errors.ts';
import { GET_CONVERSATION, GET_CONVERSATIONS, UPDATE_CONVERSATION_SHARE } from '../queries.ts';
import { displayName } from './ProjectSharingFields.tsx';
import { formatTimestamp } from './toolDisplay.tsx';

const ShareIcon = withIcon(faArrowUpFromBracket);
const PlusIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faXmark);

// "Team Berlin" → "TB"
const teamInitials = (name: string) => {
    const [first = '', second = ''] = name.split(/\s+/);
    return initialsOf({ firstName: first, lastName: second });
};

const Row = ({
    avatar,
    name,
    detail,
    meta,
    badge,
    onRemove,
    removeLabel,
}: {
    avatar: React.ReactNode;
    name: string;
    detail?: string;
    meta?: string;
    badge: string;
    onRemove?: () => void;
    removeLabel?: string;
}) => (
    <div className="flex items-center gap-3 py-2">
        {avatar}
        <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm text-content-strong">{name}</span>
            {detail && <span className="truncate text-xs text-content-medium">{detail}</span>}
            {meta && <span className="truncate text-xs text-content-weak">{meta}</span>}
        </span>
        <Tag isRemovable={false} className="flex-none py-0.5 text-xs text-content-medium">
            {badge}
        </Tag>
        {onRemove ? (
            <button
                type="button"
                aria-label={removeLabel}
                onClick={onRemove}
                className="flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded border-none bg-transparent text-content-medium transition-colors hover:bg-surface-strong hover:text-content-strong"
            >
                <RemoveIcon />
            </button>
        ) : (
            <span className="h-7 w-7 flex-none" />
        )}
    </div>
);

/** The thread header's share button (ENG-11386). Nothing shared yet: it opens the share dialog. Shared: it carries a
 *  dot and opens the Access list — owner, then everyone who can view, grouped by what they got (the whole
 *  conversation or one exchange) with the date each share was made. Every row is removable on its own; removing the
 *  last recipient of a snapshot revokes it. "Add people or teams" opens the dialog for a new snapshot. */
export const ShareAccessPopover = ({ conversation, onAdd }: { conversation: any; onAdd: () => void }) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const [open, setOpen] = useState(false);
    const [updateShare, { loading }] = useMutation(UPDATE_CONVERSATION_SHARE, {
        // the sidebar shows who a chat is shared with
        refetchQueries: [{ query: GET_CONVERSATION, variables: { id: conversation.id } }, { query: GET_CONVERSATIONS }],
    });

    const shares = conversation.shares || [];
    const isShared = shares.length > 0;

    const button = (
        <Button
            variant="ghost"
            className={classnames('relative px-2', isShared && 'bg-surface-brand-weak text-content-brand-strong')}
            aria-label={t('share.action')}
            onClick={isShared ? undefined : onAdd}
        >
            <ShareIcon />
            {isShared && (
                <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-surface-brand-strong-default ring-2 ring-surface-weak" />
            )}
        </Button>
    );
    if (!isShared) return <Tooltip content={t('share.action')}>{button}</Tooltip>;

    const update = async (share, teamId: string | null, memberIds: string[]) => {
        try {
            const { data } = await updateShare({ variables: { id: share.id, teamId, memberIds } });
            publishToast({
                description: data.updateAgentConversationShare.revoked ? t('share.revoked') : t('share.updated'),
                status: 'success',
            });
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('share.error')), status: 'error' });
        }
    };
    const memberIdsOf = (share) => share.members.map((member) => member.id);
    const sharedOn = (share) => t('share.sharedOn', { date: formatTimestamp(share.createdAt) });

    const groups = [
        { key: 'whole', label: t('share.scopeAll'), shares: shares.filter((share) => !share.sourceMessage) },
        { key: 'task', label: t('share.scopeTask'), shares: shares.filter((share) => share.sourceMessage) },
    ].filter((group) => group.shares.length > 0);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <Tooltip content={t('share.access')}>
                <Popover.Trigger asChild>{button}</Popover.Trigger>
            </Tooltip>
            <Popover.Content align="end" className="w-[26rem] p-4">
                <div className="text-xs font-medium text-content-medium">{t('share.access')}</div>
                {conversation.createdBy && (
                    <Row
                        avatar={<Avatar person={conversation.createdBy} size="md" />}
                        name={displayName(conversation.createdBy)}
                        detail={t('share.you')}
                        badge={t('share.owner')}
                    />
                )}
                <div className="mt-2 text-xs font-medium text-content-medium">{t('share.viewRights')}</div>
                <Button
                    variant="outline"
                    className="mt-2 w-full justify-start"
                    onClick={() => {
                        setOpen(false);
                        onAdd();
                    }}
                >
                    <PlusIcon className="mr-2" />
                    {t('share.addRecipient')}
                </Button>
                {groups.map((group) => (
                    <React.Fragment key={group.key}>
                        <div className="mb-1 mt-4 border-0 border-t border-solid border-line-weak pt-3 text-sm font-semibold text-content-strong">
                            {group.label}
                        </div>
                        {group.shares.map((share) => (
                            <React.Fragment key={share.id}>
                                {share.team && (
                                    <Row
                                        avatar={
                                            <Avatar size="md" title={share.team.name}>
                                                {teamInitials(share.team.name)}
                                            </Avatar>
                                        }
                                        name={share.team.name}
                                        detail={t('share.teamPeople', { count: share.team.users?.length ?? 0 })}
                                        meta={sharedOn(share)}
                                        badge={t('share.canView')}
                                        removeLabel={t('share.remove')}
                                        onRemove={loading ? undefined : () => update(share, null, memberIdsOf(share))}
                                    />
                                )}
                                {share.members.map((member) => (
                                    <Row
                                        key={member.id}
                                        avatar={<Avatar person={member} size="md" />}
                                        name={displayName(member)}
                                        detail={member.email}
                                        meta={sharedOn(share)}
                                        badge={t('share.canView')}
                                        removeLabel={t('share.remove')}
                                        onRemove={
                                            loading
                                                ? undefined
                                                : () =>
                                                      update(
                                                          share,
                                                          share.team?.id ?? null,
                                                          memberIdsOf(share).filter((id) => id !== member.id)
                                                      )
                                        }
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </React.Fragment>
                ))}
            </Popover.Content>
        </Popover>
    );
};
